import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextInput } from '@/common/components/Inputs/index';
import Checkbox from '@/common/components/Inputs/Checkbox/index';
import styles from '@/common/containers/Forms/SetPassword/styles.less';
import { getMarketingSiteUrl } from '@/app/config';
import { useLocale } from '@/common/hooks';

const initialValues = {
  password: '',
  repeatPassword: '',
};

const SetPassword = ({
  className,
  onSubmit,
  submitText = 'Save',
  withPolicy = false,
  isPending = false,
}) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const legalDisclaimerUrl = getMarketingSiteUrl('/legal-disclaimer', locale);
  const passwordReq = t(
    'Password must be at least 8 characters, contain uppercase and lowercase, numbers and special symbols'
  );
  const validation = yup.object({
    password: yup
      .string()
      .test('password', passwordReq, (value) => {
        if (!value) return false;
        if (value.length < 8) return false;
        if (!/[A-Z]+/.test(value)) return false;
        if (!/[a-z]+/.test(value)) return false;
        if (!/[0-9]+/.test(value)) return false;
        if (!/[^A-Za-z0-9]+/.test(value)) return false;
        return true;
      })
      .required(t('This field is required')),
    repeatPassword: yup
      .string()
      .required(t('Please repeat password'))
      .test('repeatPassword', t('Repeat password error'), function (value) {
        return value === this.parent.password;
      }),
    ...(withPolicy && {
      policy: yup.boolean().oneOf([true], t('Required to accept')),
      legalDisclaimer: yup
        .boolean()
        .oneOf([true], t('You must accept the Legal Disclaimer to continue.')),
    }),
  });

  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(validation),
    shouldUnregister: true,
    defaultValues: initialValues,
  });
  const { isDirty, isValid } = methods.formState;
  const repeatPasswordValue = methods.watch('repeatPassword');
  const passwordValue = methods.watch('password');

  const handleSubmit = useCallback(
    methods.handleSubmit(async (form) => {
      await onSubmit(form);
    }),
    [methods, onSubmit]
  );

  return (
    <FormProvider {...methods}>
      <Box component="form" className={className} onSubmit={handleSubmit}>
        <TextInput
          name="password"
          label={t('Create password')}
          type="password"
          tip={passwordValue ? passwordReq : ''}
          inputProps={{
            maxLength: 50,
          }}
        />
        <TextInput
          name="repeatPassword"
          label={t('Confirm password')}
          type="password"
          tip={repeatPasswordValue ? t('Both passwords must match') : ''}
          inputProps={{
            maxLength: 50,
          }}
          sx={{
            mt: 4,
          }}
        />
        {withPolicy && (
          <>
            <Checkbox
              name="policy"
              sx={{ mt: 4 }}
              label={
                <span className={styles.policy}>
                  {t('I accept the')}{' '}
                  <a
                    href="https://eac-public-files.s3.ca-central-1.amazonaws.com/EAC_Privacy_Policy.pdf"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('Privacy Policy')}
                  </a>{' '}
                  {t('and')}{' '}
                  <a
                    href="https://eac-public-files.s3.ca-central-1.amazonaws.com/EAC_Terms_and_Conditions.pdf"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('Terms of Service')}
                  </a>
                </span>
              }
            />
            <Checkbox
              name="legalDisclaimer"
              sx={{ mt: 4 }}
              inputProps={{
                size: 'small',
                sx: {
                  alignSelf: 'baseline',
                  mt: '2px',
                  p: '0',
                  pr: 2,
                },
              }}
              label={
                <span
                  className={styles.policy}
                  style={{ whiteSpace: 'normal' }}
                >
                  {t(
                    'I understand that the information provided by this product is for general informational purposes only and is not legal advice. See'
                  )}{' '}
                  <a href={legalDisclaimerUrl} target="_blank" rel="noreferrer">
                    {t('Legal Disclaimer')}
                  </a>
                </span>
              }
            />
          </>
        )}
        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={!isDirty || !isValid || isPending}
          loading={isPending}
          sx={{ mt: 6 }}
        >
          {submitText}
        </Button>
      </Box>
    </FormProvider>
  );
};

export default SetPassword;
